import type {
  Link,
  LinksData,
  ContactSubmission,
  RosterMember,
  RosterMemberInput,
  NewsArticle,
  NewsArticleInput,
  Event,
  EventInput,
  SiteText,
} from '../types'

// Dev mode: Use pre-minted admin token from environment (generated by `pnpm mint-token`)
const DEV_ADMIN_TOKEN = import.meta.env.VITE_DEV_ADMIN_TOKEN

// Safely parse JSON response — falls back to a user-friendly error if the
// server returns a non-JSON body (e.g. a Cloudflare HTML error page).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function parseJsonResponse(response: Response): Promise<any> {
  try {
    return await response.json()
  } catch {
    throw new Error(`Server error (${response.status}). Please try again.`)
  }
}

export function getHeaders(): HeadersInit {
  if (DEV_ADMIN_TOKEN) {
    return {
      Authorization: `Bearer ${DEV_ADMIN_TOKEN}`,
    }
  }
  return {}
}

// Links API
export async function fetchLinks(): Promise<{ data: LinksData; etag: string }> {
  const response = await fetch('/api/admin/links/live', {
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch links')
  }

  const data = await response.json()
  const etag = response.headers.get('ETag') || ''

  return { data, etag }
}

export async function publishLinks(
  links: Link[],
  etag: string
): Promise<{ ok: boolean; error?: string }> {
  const response = await fetch('/api/admin/links/publish', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'If-Match': etag,
      ...getHeaders(),
    },
    body: JSON.stringify({ links }),
  })

  const result = await response.json()

  if (!response.ok) {
    // Handle Zod validation errors
    if (result.error?.issues) {
      const messages = result.error.issues.map((issue: { message: string }) => issue.message)
      throw new Error(messages.join(', '))
    }
    throw new Error(result.error || 'Failed to publish')
  }

  return result
}

// Contact API
export async function fetchContactSubmissions(
  status: 'unread' | 'read' | 'responded' | 'all' = 'unread'
): Promise<{
  submissions: ContactSubmission[]
  total: number
  status: string
}> {
  const response = await fetch(`/api/admin/contact/submissions?status=${status}`, {
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch contact submissions')
  }

  return response.json()
}

export async function updateContactStatus(
  id: string,
  status: 'unread' | 'read' | 'responded'
): Promise<{ ok: boolean }> {
  const response = await fetch(`/api/admin/contact/submissions/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(),
    },
    body: JSON.stringify({ status }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || 'Failed to update status')
  }

  return result
}

export async function deleteContactSubmission(id: string): Promise<{ ok: boolean }> {
  const response = await fetch(`/api/admin/contact/submissions/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getHeaders(),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || 'Failed to delete submission')
  }

  return result
}

// Roster API
export async function fetchRosterMembers(): Promise<{ members: RosterMember[] }> {
  const response = await fetch('/api/admin/roster', {
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch roster members')
  }

  return response.json()
}

export async function fetchRosterMember(id: string): Promise<RosterMember> {
  const response = await fetch(`/api/admin/roster/${id}`, {
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch roster member')
  }

  return response.json()
}

export async function createRosterMember(data: RosterMemberInput): Promise<RosterMember> {
  const response = await fetch('/api/admin/roster', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(),
    },
    body: JSON.stringify(data),
  })

  const result = await response.json()

  if (!response.ok) {
    // Handle Zod validation errors
    if (result.error?.issues) {
      const messages = result.error.issues.map((issue: { message: string }) => issue.message)
      throw new Error(messages.join(', '))
    }
    throw new Error(result.error || 'Failed to create roster member')
  }

  return result
}

export async function updateRosterMember(
  id: string,
  data: Partial<RosterMemberInput>
): Promise<RosterMember> {
  const response = await fetch(`/api/admin/roster/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(),
    },
    body: JSON.stringify(data),
  })

  const result = await response.json()

  if (!response.ok) {
    // Handle Zod validation errors
    if (result.error?.issues) {
      const messages = result.error.issues.map((issue: { message: string }) => issue.message)
      throw new Error(messages.join(', '))
    }
    throw new Error(result.error || 'Failed to update roster member')
  }

  return result
}

export async function deleteRosterMember(id: string): Promise<{ success: boolean }> {
  const response = await fetch(`/api/admin/roster/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getHeaders(),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || 'Failed to delete roster member')
  }

  return result
}

export async function hardDeleteRosterMember(
  id: string
): Promise<{ success: boolean; deleted: boolean }> {
  const response = await fetch(`/api/admin/roster/${id}?permanent=true`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getHeaders(),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || 'Failed to permanently delete roster member')
  }

  return result
}

// News API
export async function getNewsArticles(): Promise<{ articles: NewsArticle[] }> {
  const response = await fetch('/api/admin/news', {
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch news articles')
  }

  return response.json()
}

export async function createNewsArticle(data: NewsArticleInput): Promise<NewsArticle> {
  const response = await fetch('/api/admin/news', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(),
    },
    body: JSON.stringify(data),
  })

  const result = await parseJsonResponse(response)

  if (!response.ok) {
    // Handle Zod validation errors
    if (result.error?.issues) {
      const messages = result.error.issues.map((issue: { message: string }) => issue.message)
      throw new Error(messages.join(', '))
    }
    throw new Error(result.error || 'Failed to create news article')
  }

  return result
}

export async function updateNewsArticle(
  id: string,
  data: Partial<NewsArticleInput>
): Promise<NewsArticle> {
  const response = await fetch(`/api/admin/news/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(),
    },
    body: JSON.stringify(data),
  })

  const result = await parseJsonResponse(response)

  if (!response.ok) {
    // Handle Zod validation errors
    if (result.error?.issues) {
      const messages = result.error.issues.map((issue: { message: string }) => issue.message)
      throw new Error(messages.join(', '))
    }
    throw new Error(result.error || 'Failed to update news article')
  }

  return result
}

export async function deleteNewsArticle(id: string): Promise<{ success: boolean }> {
  const response = await fetch(`/api/admin/news/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete news article')
  }

  return response.json()
}

// Events API
export async function getEvents(): Promise<{ events: Event[] }> {
  const response = await fetch('/api/admin/events', {
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch events')
  }

  return response.json()
}

export async function createEvent(data: EventInput): Promise<Event> {
  const response = await fetch('/api/admin/events', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(),
    },
    body: JSON.stringify(data),
  })

  const result = await parseJsonResponse(response)

  if (!response.ok) {
    // Handle Zod validation errors
    if (result.error?.issues) {
      const messages = result.error.issues.map((issue: { message: string }) => issue.message)
      throw new Error(messages.join(', '))
    }
    throw new Error(result.error || 'Failed to create event')
  }

  return result
}

export async function updateEvent(id: string, data: Partial<EventInput>): Promise<Event> {
  const response = await fetch(`/api/admin/events/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(),
    },
    body: JSON.stringify(data),
  })

  const result = await parseJsonResponse(response)

  if (!response.ok) {
    // Handle Zod validation errors
    if (result.error?.issues) {
      const messages = result.error.issues.map((issue: { message: string }) => issue.message)
      throw new Error(messages.join(', '))
    }
    throw new Error(result.error || 'Failed to update event')
  }

  return result
}

export async function deleteEvent(id: string): Promise<{ success: boolean }> {
  const response = await fetch(`/api/admin/events/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete event')
  }

  return response.json()
}

// Text API
export async function fetchText(
  locale: string = 'en'
): Promise<{ data: SiteText; etag: string; locale: string }> {
  const response = await fetch(`/api/admin/text?locale=${locale}`, {
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch site text')
  }

  return response.json()
}

export async function publishText(
  data: SiteText,
  etag: string,
  locale: string = 'en'
): Promise<{ success: boolean; etag?: string; version?: number; error?: string }> {
  const response = await fetch('/api/admin/text', {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(),
    },
    body: JSON.stringify({ data, etag, locale }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || 'Failed to publish site text')
  }

  return result
}

export async function getTextBackups(
  locale: string = 'en'
): Promise<{ backups: Array<{ key: string; timestamp: string; size: number }> }> {
  const response = await fetch(`/api/admin/text/backups?locale=${locale}`, {
    credentials: 'include',
    headers: getHeaders(),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch text backups')
  }

  return response.json()
}
